/**
 * Page-effects consumer — the GENERIC executor that turns copilot
 * fill / fill_field / submit / highlight / open_form actions into REAL,
 * visible effects on the page that is currently on screen.
 *
 * Historically the action executor only dispatched `copilot:*` CustomEvents
 * and returned `status:'executed'`, but NO page ever subscribed to them, so a
 * confirmed action logged "exécutée" while doing nothing. This module closes
 * that gap by acting on the page context registered through `useCopilotPage`
 * (see context-registry.ts):
 *
 *   1. Imperative handlers — a page MAY expose `handlers` (fillField/fillForm/
 *      submitForm/openForm/resolveField) on its PageContext. These are the
 *      authoritative, framework-native way to react to copilot actions.
 *   2. DOM heuristics — when no handler is provided we resolve the target from
 *      the registered `formSchema` (by data-copilot-field / name / id / <label>
 *      text) and act on it directly (native value setters + React-compatible
 *      input/change events, `requestSubmit()`, scroll + outline).
 *
 * If neither a handler nor a DOM target can be found, the effect reports
 * `status:'skipped'` with an honest "no target" detail instead of a false
 * "executed". Pages that opt in (handlers or `data-copilot-*` attributes) get
 * the full effect; pages that don't at least keep an honest timeline.
 */

import { getCurrent } from './context-registry';
import type { ExecutionResult } from './action-executor';
import type { CopilotActionTarget, PageContext } from './types';
import { tr } from '@/i18n';

type FieldControl = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

const NO_DOM: ExecutionResult = { status: 'skipped', detail: tr('shell.features.copilotPageEffects.domUnavailable') };

function hasDom(): boolean {
  return typeof document !== 'undefined';
}

function cssEscape(value: string): string {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') return CSS.escape(value);
  // Minimal fallback: escape anything that is not a safe identifier char.
  return value.replace(/[^\w-]/g, '\\$&');
}

/**
 * Narrow the search to the registered form/page container when the page
 * exposes one (`[data-copilot-form]`, `#formId`, `form[name]`,
 * `[data-copilot-page]`); otherwise fall back to the whole document.
 */
function scopeRoot(ctx: PageContext | null, formId?: string): ParentNode {
  if (!hasDom()) return document;
  const fid = formId ?? ctx?.formSchema?.formId;
  if (fid) {
    const esc = cssEscape(fid);
    const scoped = document.querySelector(
      `[data-copilot-form="${esc}"], form#${esc}, #${esc}, form[name="${esc}"]`,
    );
    if (scoped) return scoped;
  }
  if (ctx?.pageId) {
    const page = document.querySelector(`[data-copilot-page="${cssEscape(ctx.pageId)}"]`);
    if (page) return page;
  }
  return document;
}

function resolveByLabel(root: ParentNode, labelText: string): HTMLElement | null {
  const norm = (s: string) => s.trim().toLowerCase();
  const target = norm(labelText);
  if (!target) return null;
  const labels = Array.from(root.querySelectorAll('label'));
  let match =
    labels.find((l) => norm(l.textContent ?? '') === target) ??
    labels.find((l) => norm(l.textContent ?? '').startsWith(target));
  if (!match) return null;
  const forId = match.getAttribute('for');
  if (forId) {
    const byId = document.getElementById(forId);
    if (byId) return byId;
  }
  return match.querySelector<HTMLElement>('input, select, textarea');
}

/**
 * Resolve a registered field to its DOM control. Resolution order:
 * page-supplied `resolveField` handler → data-copilot-field → name → id →
 * matching `<label>` text (from the registered schema label).
 */
function resolveFieldElement(
  ctx: PageContext | null,
  field: string,
  root: ParentNode,
): HTMLElement | null {
  const viaHandler = ctx?.handlers?.resolveField?.(field);
  if (viaHandler) return viaHandler;

  const esc = cssEscape(field);
  const direct = root.querySelector<HTMLElement>(
    `[data-copilot-field="${esc}"], [name="${esc}"], #${esc}`,
  );
  if (direct) return direct;

  const schemaField = ctx?.formSchema?.fields.find((f) => f.name === field);
  if (schemaField?.label) {
    const byLabel = resolveByLabel(root, schemaField.label);
    if (byLabel) return byLabel;
    const byAria = root.querySelector<HTMLElement>(
      `[aria-label="${cssEscape(schemaField.label)}"]`,
    );
    if (byAria) return byAria;
  }
  return null;
}

function isFieldControl(el: Element | null): el is FieldControl {
  return (
    el instanceof HTMLInputElement ||
    el instanceof HTMLTextAreaElement ||
    el instanceof HTMLSelectElement
  );
}

/** Set a property through the prototype setter so React's value tracker fires. */
function setNativeProp(el: FieldControl, prop: 'value' | 'checked', value: unknown): void {
  const proto = Object.getPrototypeOf(el) as object;
  const desc = Object.getOwnPropertyDescriptor(proto, prop);
  if (desc?.set) {
    desc.set.call(el, value);
  } else {
    (el as unknown as Record<string, unknown>)[prop] = value;
  }
}

const foldText = (s: string): string =>
  s.normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toLowerCase();

/**
 * The option of a <select> a model-proposed value designates: the option `value` (exact, then
 * case/accent-insensitive) or its visible label. The model often says « brouillon » / « Alerter »
 * (what the user reads) where the option value is `draft` / `alert`; a raw `select.value = 'brouillon'`
 * matches nothing and silently resets the select to '' (→ a 422 on submit).
 */
export function matchSelectOption(sel: HTMLSelectElement, value: unknown): HTMLOptionElement | null {
  const want = value == null ? '' : String(value);
  const opts = Array.from(sel.options);
  const folded = foldText(want);
  return (
    opts.find((o) => o.value === want) ??
    opts.find((o) => foldText(o.value) === folded) ??
    opts.find((o) => foldText(o.textContent ?? '') === folded) ??
    (folded ? opts.find((o) => foldText(o.textContent ?? '').startsWith(folded)) : undefined) ??
    null
  );
}

/** Returns false when the value designates no option of a <select> (the control is left untouched). */
function setControlValue(el: FieldControl, value: unknown): boolean {
  if (el instanceof HTMLSelectElement) {
    const opt = matchSelectOption(el, value);
    if (!opt) return false;
    value = opt.value;
  }
  if (el instanceof HTMLInputElement && (el.type === 'checkbox' || el.type === 'radio')) {
    const checked = value !== false && value !== 'false' && value !== '0' && value != null && value !== '';
    setNativeProp(el, 'checked', Boolean(checked));
  } else {
    setNativeProp(el, 'value', value == null ? '' : String(value));
  }
  el.dispatchEvent(new Event('input', { bubbles: true }));
  el.dispatchEvent(new Event('change', { bubbles: true }));
  return true;
}

// --- Highlight (scroll-to + outline pulse) ---------------------------------

const HIGHLIGHT_STYLE_ID = 'copilot-highlight-styles';
const HIGHLIGHT_CLASS = 'copilot-highlight-target';
const HIGHLIGHT_DURATION = 2200;
const highlightTimers = new WeakMap<HTMLElement, number>();

function ensureHighlightStyles(): void {
  if (!hasDom() || document.getElementById(HIGHLIGHT_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = HIGHLIGHT_STYLE_ID;
  style.textContent = [
    '@keyframes copilot-highlight-pulse {',
    '  0% { box-shadow: 0 0 0 0 rgba(99,102,241,0.55); }',
    '  70% { box-shadow: 0 0 0 8px rgba(99,102,241,0); }',
    '  100% { box-shadow: 0 0 0 0 rgba(99,102,241,0); }',
    '}',
    `.${HIGHLIGHT_CLASS} {`,
    '  outline: 2px solid rgb(99 102 241) !important;',
    '  outline-offset: 2px;',
    '  border-radius: 6px;',
    '  scroll-margin: 96px;',
    '  animation: copilot-highlight-pulse 1.1s ease-out 2;',
    '}',
  ].join('\n');
  document.head.appendChild(style);
}

function highlightElement(el: HTMLElement): void {
  ensureHighlightStyles();
  const previous = highlightTimers.get(el);
  if (previous) window.clearTimeout(previous);
  el.classList.add(HIGHLIGHT_CLASS);
  try {
    el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
  } catch {
    el.scrollIntoView();
  }
  const timer = window.setTimeout(() => {
    el.classList.remove(HIGHLIGHT_CLASS);
    highlightTimers.delete(el);
  }, HIGHLIGHT_DURATION);
  highlightTimers.set(el, timer);
}

// --- Public performers ------------------------------------------------------

/** Fill one or more registered fields with real, React-compatible value sets. */
export function performFill(fields: Record<string, unknown>, formId?: string): ExecutionResult {
  if (!hasDom()) return NO_DOM;
  const ctx = getCurrent();
  const names = Object.keys(fields);

  // A whole-form handler wins outright.
  if (ctx?.handlers?.fillForm) {
    const handled = ctx.handlers.fillForm(fields);
    if (handled !== false) {
      return { status: 'executed', detail: tr('shell.features.copilotPageEffects.lengthFieldSFilled', { length: names.length }) };
    }
  }

  const root = scopeRoot(ctx, formId);
  const filled: string[] = [];
  const missing: string[] = [];
  let firstFilled: HTMLElement | null = null;

  for (const name of names) {
    if (ctx?.handlers?.fillField) {
      const handled = ctx.handlers.fillField(name, fields[name]);
      if (handled !== false) {
        filled.push(name);
        continue;
      }
    }
    const el = resolveFieldElement(ctx, name, root);
    if (isFieldControl(el)) {
      if (!setControlValue(el, fields[name])) {
        missing.push(name);
        continue;
      }
      filled.push(name);
      if (!firstFilled) firstFilled = el;
    } else {
      missing.push(name);
    }
  }

  if (filled.length === 0) {
    return {
      status: 'skipped',
      detail:
        names.length === 0
          ? tr('shell.features.copilotPageEffects.noFieldProvided')
          : tr('shell.features.copilotPageEffects.noTargetFieldFoundV', { v: missing.join(', ') }),
    };
  }
  if (firstFilled) highlightElement(firstFilled);
  const detail =
    missing.length > 0
      ? tr('shell.features.copilotPageEffects.fieldsFilledMissing', { filled: filled.length, missing: missing.length })
      : tr('shell.features.copilotPageEffects.fieldsFilled', { filled: filled.length });
  return { status: 'executed', detail };
}

/**
 * Resolve a target to a DOM element, in order of specificity:
 * explicit `selector` → registered `field` → `form_id` container → `page_id`
 * marker. Shared by highlight and scroll_to.
 */
function resolveTargetElement(
  ctx: PageContext | null,
  target?: CopilotActionTarget,
): HTMLElement | null {
  if (!hasDom() || !target) return null;
  if (target.selector) {
    try {
      const bySelector = document.querySelector<HTMLElement>(target.selector);
      if (bySelector) return bySelector;
    } catch {
      // Invalid selector string — fall through to context-based resolution.
    }
  }
  const root = scopeRoot(ctx, target.form_id);
  if (target.field) {
    const byField = resolveFieldElement(ctx, target.field, root);
    if (byField) return byField;
  }
  const formId = target.form_id ?? ctx?.formSchema?.formId;
  if (formId) {
    const esc = cssEscape(formId);
    const byForm = document.querySelector<HTMLElement>(
      `[data-copilot-form="${esc}"], form#${esc}, #${esc}, form[name="${esc}"]`,
    );
    if (byForm) return byForm;
  }
  const pageId = target.page_id ?? ctx?.pageId;
  if (pageId) {
    const byPage = document.querySelector<HTMLElement>(`[data-copilot-page="${cssEscape(pageId)}"]`);
    if (byPage) return byPage;
  }
  return null;
}

function targetLabel(target?: CopilotActionTarget): string {
  return target?.selector ?? target?.field ?? target?.form_id ?? target?.page_id ?? tr('shell.features.copilotPageEffects.item');
}

/** Scroll to and outline the targeted selector / field / form / page element. */
export function performHighlight(target?: CopilotActionTarget): ExecutionResult {
  if (!hasDom()) return NO_DOM;
  const el = resolveTargetElement(getCurrent(), target);
  if (!el) return { status: 'skipped', detail: tr('shell.features.copilotPageEffects.noElementToHighlight') };
  highlightElement(el);
  return { status: 'executed', detail: tr('shell.features.copilotPageEffects.highlightingV', { v: targetLabel(target) }) };
}

/** Scroll the targeted selector / field / form / page element into view (no outline). */
export function performScrollTo(target?: CopilotActionTarget): ExecutionResult {
  if (!hasDom()) return NO_DOM;
  const el = resolveTargetElement(getCurrent(), target);
  if (!el) return { status: 'skipped', detail: tr('shell.features.copilotPageEffects.noElementToScrollTo') };
  try {
    el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
  } catch {
    try {
      el.scrollIntoView();
    } catch {
      // jsdom / environments without scrollIntoView — resolution still succeeded.
    }
  }
  return { status: 'executed', detail: tr('shell.features.copilotPageEffects.scrollingToV', { v: targetLabel(target) }) };
}

/** Open a named modal/dialog (handler first, then a marked `data-copilot-open-modal` opener). */
export function performOpenModal(target?: CopilotActionTarget, payload?: unknown): ExecutionResult {
  if (!hasDom()) return NO_DOM;
  const ctx = getCurrent();
  const modalId = target?.modal_id;

  if (ctx?.handlers?.openModal) {
    const handled = ctx.handlers.openModal(modalId, payload);
    if (handled !== false) {
      return { status: 'executed', detail: tr('shell.features.copilotPageEffects.openingTheVWindow', { v: modalId ?? '(courante)' }) };
    }
  }
  if (modalId) {
    const opener = document.querySelector<HTMLElement>(
      `[data-copilot-open-modal="${cssEscape(modalId)}"]`,
    );
    if (opener) {
      opener.click();
      return { status: 'executed', detail: tr('shell.features.copilotPageEffects.openingTheModalidWindow', { modalId }) };
    }
  }
  return { status: 'skipped', detail: tr('shell.features.copilotPageEffects.noWindowOpenerRegistered') };
}

function extractRowIds(payload: unknown): string[] {
  const raw =
    Array.isArray(payload)
      ? payload
      : payload !== null && typeof payload === 'object' && 'row_ids' in payload
        ? (payload as { row_ids?: unknown }).row_ids
        : undefined;
  return Array.isArray(raw) ? raw.map((v) => String(v)).filter(Boolean) : [];
}

/** Select the given table rows (handler first, then per-row checkboxes in the DOM). */
export function performSelectRows(target?: CopilotActionTarget, payload?: unknown): ExecutionResult {
  if (!hasDom()) return NO_DOM;
  const ctx = getCurrent();
  const rowIds = extractRowIds(payload);

  if (ctx?.handlers?.selectRows) {
    const handled = ctx.handlers.selectRows(rowIds, target?.table_id);
    if (handled !== false) {
      return { status: 'executed', detail: tr('shell.features.copilotPageEffects.lengthRowSSelected', { length: rowIds.length }) };
    }
  }
  if (rowIds.length === 0) return { status: 'skipped', detail: tr('shell.features.copilotPageEffects.noRowToSelect') };

  const tableId = target?.table_id;
  let root: ParentNode = document;
  if (tableId) {
    const esc = cssEscape(tableId);
    root =
      document.querySelector(
        `[data-copilot-table="${esc}"], table#${esc}, #${esc}, table[data-table="${esc}"]`,
      ) ?? document;
  }

  let selected = 0;
  for (const id of rowIds) {
    const esc = cssEscape(id);
    const rowEl = root.querySelector<HTMLElement>(
      `[data-copilot-row="${esc}"], [data-row-id="${esc}"], tr#${esc}, #row-${esc}`,
    );
    const box = rowEl?.querySelector<HTMLInputElement>('input[type="checkbox"]') ?? null;
    if (!box) continue;
    if (!box.checked) {
      setNativeProp(box, 'checked', true);
      box.dispatchEvent(new Event('input', { bubbles: true }));
      box.dispatchEvent(new Event('change', { bubbles: true }));
    }
    selected += 1;
  }
  if (selected === 0) return { status: 'skipped', detail: tr('shell.features.copilotPageEffects.noTargetRowFound') };
  return { status: 'executed', detail: tr('shell.features.copilotPageEffects.selectedRowSSelected', { selected }) };
}

/** Submit the registered form (page handler first, then a resolvable <form>). */
export function performSubmit(formId?: string): ExecutionResult {
  if (!hasDom()) return NO_DOM;
  const ctx = getCurrent();
  const fid = formId ?? ctx?.formSchema?.formId;

  if (ctx?.handlers?.submitForm) {
    const handled = ctx.handlers.submitForm(fid);
    if (handled !== false) {
      return { status: 'executed', detail: tr('shell.features.copilotPageEffects.formVSubmitted', { v: fid ?? '(courant)' }) };
    }
  }

  let form: HTMLFormElement | null = null;
  if (fid) {
    const esc = cssEscape(fid);
    form = document.querySelector<HTMLFormElement>(
      `form#${esc}, form[name="${esc}"], form[data-copilot-form="${esc}"]`,
    );
    if (!form) {
      const container = document.querySelector(`[data-copilot-form="${esc}"], #${esc}`);
      if (container) {
        form = container.closest('form') ?? container.querySelector<HTMLFormElement>('form');
      }
    }
  }
  if (!form) {
    return { status: 'skipped', detail: tr('shell.features.copilotPageEffects.noFormToSubmitSubmitformHandler') };
  }
  if (typeof form.requestSubmit === 'function') form.requestSubmit();
  else form.submit();
  return { status: 'executed', detail: tr('shell.features.copilotPageEffects.formVSubmitted', { v: fid ?? '(courant)' }) };
}

/** Trigger the registered "open form" action (handler or a marked opener). */
export function performOpenForm(target?: CopilotActionTarget, payload?: unknown): ExecutionResult {
  if (!hasDom()) return NO_DOM;
  const ctx = getCurrent();
  const fid = target?.form_id ?? ctx?.formSchema?.formId;

  if (ctx?.handlers?.openForm) {
    const handled = ctx.handlers.openForm(payload);
    if (handled !== false) {
      return { status: 'executed', detail: tr('shell.features.copilotPageEffects.openingTheVForm', { v: fid ?? '(courant)' }) };
    }
  }
  if (fid) {
    const opener = document.querySelector<HTMLElement>(
      `[data-copilot-open-form="${cssEscape(fid)}"]`,
    );
    if (opener) {
      opener.click();
      return { status: 'executed', detail: tr('shell.features.copilotPageEffects.openingTheFidForm', { fid }) };
    }
  }
  return { status: 'skipped', detail: tr('shell.features.copilotPageEffects.noFormOpenerRegistered') };
}
